import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TherapyModality } from '@/hooks/useTherapyModalities';
import { TherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import StarRating from '@/components/ui/star-rating';
import { Clock, Users, Award, Building2, Sparkles, Target, Calendar } from 'lucide-react';

interface TherapyCardProps {
  therapy: TherapyModality;
  stats?: TherapyModalityStats;
}

const TherapyCard: React.FC<TherapyCardProps> = ({ therapy, stats }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language === 'zh';
  
  // Display content based on language
  const name = isZh ? therapy.name_cn : therapy.name_en;
  const mechanism = isZh ? therapy.mechanism_cn : therapy.mechanism_en;

  const getEvidenceBadgeColor = (level: string | null) => {
    switch (level) {
      case 'A': return 'bg-green-100 text-green-800 border-green-200';
      case 'B': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'C': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'D': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getEvidenceLabel = (level: string | null) => {
    switch (level) {
      case 'A': return t('evidenceLevels.A');
      case 'B': return t('evidenceLevels.B');
      case 'C': return t('evidenceLevels.C');
      case 'D': return t('evidenceLevels.D');
      default: return level;
    }
  };

  const handleBookNow = () => {
    // Navigate to browse page with therapy filter
    navigate(`/browse?therapy=${therapy.id}`);
  };

  const practitionerCount = stats?.practitioner_count ?? 0;
  const totalSessions = stats?.total_sessions ?? 0;
  const averageRating = stats?.average_rating ?? null;
  const hasAvailablePractitioners = practitionerCount > 0;

  return (
    <div className="bg-white rounded-lg border border-amber-100 p-4 space-y-3">
      {/* Header with name and badges */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-earth-900 break-words">{name}</h4>
          {therapy.abbreviation && (
            <span className="text-xs text-earth-500">({therapy.abbreviation})</span>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap flex-shrink-0">
          {therapy.evidence_level && (
            <Badge variant="outline" className={`text-xs ${getEvidenceBadgeColor(therapy.evidence_level)}`}>
              {getEvidenceLabel(therapy.evidence_level)}
            </Badge>
          )}
          {therapy.stream_type && (
            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
              {therapy.stream_type}
            </Badge>
          )}
        </div>
      </div>

      {/* Mechanism */}
      {mechanism && (
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">{t('therapy.mechanism')}</span>
            <p className="text-sm text-earth-700">{mechanism}</p>
          </div>
        </div>
      )}

      {/* Procedures */}
      {therapy.procedures && (
        <div className="flex items-start gap-2">
          <Target className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">{t('therapy.procedures')}</span>
            <p className="text-sm text-earth-700">{therapy.procedures}</p>
          </div>
        </div>
      )}

      {/* Duration and Sessions */}
      <div className="flex flex-wrap gap-4 text-sm">
        {therapy.session_length_minutes && (
          <div className="flex items-center gap-1.5 text-earth-600">
            <Clock className="h-4 w-4 text-green-500" />
            <span>{therapy.session_length_minutes} {t('therapy.minutes')}</span>
          </div>
        )}
        {therapy.recommended_number_of_sessions && (
          <div className="flex items-center gap-1.5 text-earth-600">
            <Users className="h-4 w-4 text-indigo-500" />
            <span>{therapy.recommended_number_of_sessions} {t('therapy.sessionsLabel')}</span>
          </div>
        )}
      </div>

      {/* Certifications */}
      {therapy.certifications && (
        <div className="flex items-start gap-2">
          <Award className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">{t('therapy.certifications')}</span>
            <p className="text-sm text-earth-700">{therapy.certifications}</p>
          </div>
        </div>
      )}

      {/* Organizations */}
      {therapy.organizations && (
        <div className="flex items-start gap-2">
          <Building2 className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">{t('therapy.organizations')}</span>
            <p className="text-sm text-earth-700">{therapy.organizations}</p>
          </div>
        </div>
      )}

      {/* Target Domain */}
      {therapy.target_domain && (
        <div className="text-xs text-earth-500 pt-2 border-t border-amber-50">
          {t('therapy.targetDomain')}: {therapy.target_domain}
        </div>
      )}

      {/* Platform Statistics Section */}
      <div className="pt-3 border-t border-amber-100 space-y-2">
        {/* Practitioners Available */}
        <div className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4 text-teal-500 flex-shrink-0" />
          <span className={hasAvailablePractitioners ? 'text-earth-700' : 'text-earth-400'}>
            {hasAvailablePractitioners 
              ? t('stats.practitionersAvailable', { count: practitionerCount })
              : t('stats.noPractitioners')
            }
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 text-sm">
          <StarRating rating={averageRating} size="sm" showValue={true} />
        </div>

        {/* Total Sessions */}
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          <span className={totalSessions > 0 ? 'text-earth-700' : 'text-earth-400'}>
            {totalSessions > 0 
              ? t('stats.totalSessions', { count: totalSessions })
              : t('stats.noSessions')
            }
          </span>
        </div>

        {/* Book Now Button */}
        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            variant={hasAvailablePractitioners ? 'default' : 'outline'}
            disabled={!hasAvailablePractitioners}
            onClick={handleBookNow}
            className={!hasAvailablePractitioners ? 'opacity-50 cursor-not-allowed' : ''}
          >
            {t('stats.bookNow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TherapyCard;
